import React, { useCallback, useEffect, useRef, useState } from "react";
import { BusyIdleView, calculateTimeByPosition, getTimeWithFixedMinute } from "./BusyIdleView";
import dayjs, { Dayjs } from "dayjs";
import { getScheduleListByDateAndUsersAndGroupByUser } from "@/api/calendar";
import { Button, Drawer, Tooltip, message } from "antd";
import { BusyIdleViewHeader, ScheduleByDateAndUsersProps } from "../BusyIdleHeader";
import { connect } from "dvajs";
import Bus from "@/utils/bus";
import { useTranslation } from "react-i18next";
import IconFont from "@/components/icon";
import { CreateModalFrom } from "..";

export interface BusyIdleParticipant {
  app: string;
  ddAppId: string;
  userId: string;
  teamId: string;
  realName: string;
  titleName: string; // 单位-部门-职务信息
  userType: string;
  headImg?: string;
  labelInfoList?: [];
}

function BusyIdleViewDraw(props: any) {
  const { userData, openCreateModal } = props;
  const { t } = useTranslation("calendar");
  const [schedulesByUserList, setSchedulesByUserList] = useState<ScheduleByDateAndUsersProps[]>([]);
  const schedulePositionInfo: any = useRef([]);
  const busyIdleDivEl: any = useRef(null);
  const busyIdleTimeBlock: any = useRef(null);
  const scrollRef: any = useRef(null);
  const visitUser: any = useRef(null);
  const scheduleViewDrawRef = useRef(null);
  let curTime = dayjs();
  const [timeObj, setTimeObj] = useState({
    selTime: curTime,
    beginTime: getTimeWithFixedMinute(curTime),
    endTime: getTimeWithFixedMinute(curTime.add(30, "minute"))
  });
  const [visible, setVisible] = useState(false);

  const getBusyIdleScheduleList = useCallback(
    (time: Dayjs, userId: string) => {
      const userIds =
        userData?.userId && userData?.userId !== userId ? [userData?.userId, userId] : [userId];
      // 每次创建人或参与人变更 都重新拉取忙闲数据
      getScheduleListByDateAndUsersAndGroupByUser({
        beginDate: time.startOf("day").valueOf(),
        endDate: time.endOf("day").valueOf(),
        userIds
      })
        .then((res: any) => {
          setTimeObj({
            ...timeObj,
            beginTime: getTimeWithFixedMinute(time),
            endTime: getTimeWithFixedMinute(time.add(30, "minute")),
            selTime: time
          });
          setSchedulesByUserList(res.schedulesByUser || []);
        })
        .catch((err) => {
          message.error(err.message?.toString() || "服务异常");
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userData.userId]
  );

  const today = () => {
    getBusyIdleScheduleList(getTimeWithFixedMinute(dayjs()), visitUser.current?.userId);
  };

  const handleBusyIdleTimeChange = (add = true) => {
    const temp = add ? timeObj.selTime.add(1, "day") : timeObj.selTime.subtract(1, "day");
    getBusyIdleScheduleList(temp, visitUser.current?.userId);
  };

  const openBusyIdleViewDraw = (participant: BusyIdleParticipant) => {
    visitUser.current = participant;
    curTime = dayjs();
    getBusyIdleScheduleList(curTime, participant.userId);
    setVisible(true);
  };

  const closeHandle = () => {
    visitUser.current = null;
    setVisible(false);
  };

  const updateTimeState = (beginTime: Dayjs, endTime: Dayjs) => {
    setTimeObj({
      ...timeObj,
      beginTime,
      endTime
    });
  };

  useEffect(() => {
    if (!visible || !busyIdleTimeBlock.current) return;
    setTimeout(() => {
      calculateTimeByPosition(
        timeObj.beginTime,
        timeObj.endTime,
        timeObj.selTime,
        schedulePositionInfo.current,
        busyIdleTimeBlock.current
      );
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, timeObj.beginTime, timeObj.endTime, schedulesByUserList, busyIdleTimeBlock.current]);

  useEffect(() => {
    Bus.on("calendar:openBusyIdleViewDraw", openBusyIdleViewDraw);
    return () => {
      Bus.off("calendar:openBusyIdleViewDraw", openBusyIdleViewDraw);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) {
    return null;
  }
  return (
    <Drawer
      title={
        <div className="create-schedule-view-header">
          <div className="create-view-header">
            <span>查看日历</span>
            <IconFont
              type="iconty_close"
              className="close-drawer app-region-none"
              onClick={() => {
                closeHandle();
              }}
            />
          </div>
          <BusyIdleViewHeader
            t={t}
            time={timeObj.selTime}
            today={today}
            handleBusyIdleTimeChange={handleBusyIdleTimeChange}
          >
            <Button
              onClick={() => {
                openCreateModal({
                  visible: true,
                  title: "",
                  beginTime: timeObj.beginTime,
                  endTime: timeObj.endTime,
                  createModalFrom: CreateModalFrom.DragCreate,
                  participant: [visitUser.current]
                });
                closeHandle();
              }}
              type="text"
              size="small"
              className="create-button app-region-none"
              clstag="pageclick|keycount|xtbg_calhome_1640073907694|24"
            >
              <Tooltip title={t("create-new-schedule")}>
                <IconFont type="iconbtn_add" />
              </Tooltip>
            </Button>
          </BusyIdleViewHeader>
        </div>
      }
      width={450}
      placement="right"
      className="busyidle-drawer"
      onClose={() => {
        setVisible(false);
      }}
      visible={visible}
      closable={false}
      destroyOnClose={true}
      maskClosable={true}
    >
      <div ref={scheduleViewDrawRef} className="schedule-view-draw">
        <BusyIdleView
          selTime={timeObj.selTime}
          beginTime={timeObj.beginTime}
          endTime={timeObj.endTime}
          schedulesByUserList={schedulesByUserList}
          scrollRef={scrollRef}
          busyIdleDivEl={busyIdleDivEl}
          busyIdleTimeBlock={busyIdleTimeBlock}
          schedulePositionInfo={schedulePositionInfo}
          scheduleViewDrawRef={scheduleViewDrawRef}
          updateTimeState={updateTimeState}
        />
      </div>
    </Drawer>
  );
}

function mapStateToProps({ user }: any) {
  return {
    userData: user.userData.user
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    openCreateModal(data: any) {
      dispatch({ type: "calendar/openCreateModal", payload: data });
    }
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(BusyIdleViewDraw);
